import { asArray } from './promoPrimitives';

// Which phase an offer is displayed in (#23521). The period classification itself stays legacy (sfaHelper.campaign
// negoPeriodLevel / storePeriodLevel, shared with other screens) : the snapshot carries its raw result, in
// `campaign.level` and `snapshot.localLevels[offerKey]`. This module only applies the step's view rules on top of it.
// A consulted visit is classified at its own date (`snapshot.now`) : it shows what was negotiated or stated then, where
// the legacy step always showed the statement.

export const PHASE = Object.freeze({ NEGO: 'nego', PENDING: 'pending', STATEMENT: 'statement', NONE: 'none' });

/** Raw sfaHelper levels, used by the status bars (they never apply the pending override). */
export function rawLevels(snapshot, campaign, offerKey = null) {
	if (campaign?.promoLocal && offerKey) return snapshot?.localLevels?.[offerKey] || { nego: 0, store: 0 };
	return campaign?.level || { nego: 0, store: 0 };
}

/** Levels as the step's view sees them : a national campaign between its nego and store periods is still negotiated (TFS#20280). */
export function viewLevels(snapshot, campaign, offerKey = null) {
	if (!campaign?.promoLocal && campaign?.pending) return { nego: 3, store: rawLevels(snapshot, campaign).store };
	return rawLevels(snapshot, campaign, offerKey);
}

/**
 * Display phase of an offer (template HTM:479 / 653 / 963 / 1134) : a national offer is negotiated while the nego period
 * runs and the store period has not started, the store period wins when both overlap ; a local offer follows its own dates.
 */
export function offerPhase(snapshot, campaign, offerKey = null) {
	const { nego, store } = viewLevels(snapshot, campaign, offerKey);
	if (campaign?.promoLocal) {
		if (nego === 3) return PHASE.NEGO;
		if (store === 3) return PHASE.STATEMENT;
		return PHASE.NONE;
	}
	if (store === 3) return PHASE.STATEMENT;
	if (nego === 3) return campaign?.pending ? PHASE.PENDING : PHASE.NEGO;
	return PHASE.NONE;
}

export const isNegoPhase = (phase) => phase === PHASE.NEGO || phase === PHASE.PENDING;

/** `isCampaignInNegoPeriod` (JS:655) : raw campaign nego level, used by the national checks of isValid and by the promo plan switch. */
export const isCampaignInNegoPeriod = (campaign) => (campaign?.level?.nego || 0) > 0;

/** Phase of a campaign header : a local campaign is in its statement once an offer is and none is negotiated any more. */
export function campaignPhase(snapshot, campaign) {
	if (!campaign?.promoLocal) return offerPhase(snapshot, campaign);
	const phases = asArray(snapshot?.model?.offerInfos)
		.filter((oi) => oi.local && oi.campaignId === campaign.id)
		.map((oi) => offerPhase(snapshot, campaign, oi._key));
	return phases.includes(PHASE.STATEMENT) && !phases.includes(PHASE.NEGO) ? PHASE.STATEMENT : PHASE.NEGO;
}

/** Campaign subtitle key (spec §3), resolved through the backend resources. */
export function campaignPhaseKey(snapshot, campaign) {
	if (snapshot?.consultation) return 'PromoPhaseConsultation';
	if (campaign?.promoLocal) return campaignPhase(snapshot, campaign) === PHASE.STATEMENT ? 'PromoPhaseLocalStore' : 'PromoPhaseLocalNego';
	const phase = offerPhase(snapshot, campaign);
	if (phase === PHASE.STATEMENT) return 'PromoPhaseStoreInProgress';
	if (phase === PHASE.PENDING) return 'CampaignFixNegoPeriod';
	if (phase === PHASE.NEGO) return 'PromoPhaseNegoInProgress';
	return '';
}

/** Columns of the products table, per phase (spec §5). */
export function columnsOf(phase) {
	if (phase === PHASE.STATEMENT) return ['product', 'presence', 'statedVolume', 'price', 'location', 'highlight', 'additional'];
	if (isNegoPhase(phase)) return ['product', 'negotiated', 'location', 'highlight', 'additional', 'value'];
	return ['product'];
}

/**
 * Widest frame of the compact (mobile / tablet) layout of the step. Wider than the legacy touch breakpoint
 * (`windowHelper.isTablet()`, 1080px) because the desktop product table gets cut below 1370px. Above it, the step is the desktop table.
 */
export const COMPACT_LAYOUT_MAX_WIDTH = 1280;

export const isCompactPromoLayout = (frameWidth) => Number.isFinite(frameWidth) && frameWidth <= COMPACT_LAYOUT_MAX_WIDTH;
